import React, { useEffect, useRef, useState } from "react";
import { Icons } from "atoms/constants/icons";
import { LoaderSizes } from "atoms/constants/loader-sizes";
import { LoaderStyles } from "atoms/constants/loader-styles";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import { BlockRecord } from "models/view-models/block-record";
import { t } from "utilities/localization-utils";
import { HideIfReadonly } from "utilities/contexts/readonly/hide-if-readonly";
import { PageBlockEditor } from "organisms/page-blocks/active-page-block/page-block-editor";
import { SaveState } from "models/enumerations/save-state";
import { AdditionalBlockData } from "models/interfaces/additional-block-data/additional-block-data";

export interface ActivePageBlockProps {
    additionalBlockData: AdditionalBlockData;
    block: BlockRecord;
    onBlockModified: (updatedBlock: BlockRecord) => void;
    onBlockSaved: (updatedBlock: BlockRecord) => void;
    onDelete: (block: BlockRecord) => Promise<void>;
    onMoveUp: (block: BlockRecord) => Promise<void>;
    onMoveDown: (block: BlockRecord) => Promise<void>;
    onDone?: () => void;
}

const CSS_CLASS_NAME = "c-active-page-block";
export const ACTIVE_PAGE_BLOCK_TEST_ID = "active-page-block";

const ActivePageBlock = ({
    additionalBlockData,
    block,
    onBlockModified,
    onBlockSaved,
    onDelete,
    onDone,
    onMoveDown,
    onMoveUp,
}: ActivePageBlockProps) => {
    const [saveState, setSaveState] = useState<SaveState>(SaveState.NotSaving);
    const blockRef = useRef<HTMLDivElement>(null);

    const handleBlockModified = (updatedBlock: BlockRecord) => {
        onBlockModified(updatedBlock);

        setSaveState(SaveState.Saving);
    };

    const handleBlockSaving = () => setSaveState(SaveState.Saving);

    const handleBlockSaved = (updatedBlock: BlockRecord): void => {
        onBlockSaved(updatedBlock);

        setSaveState(SaveState.Saved);
    };

    useEffect(() => {
        blockRef.current?.scrollIntoView();
    }, []);

    return (
        <HideIfReadonly>
            <div
                ref={blockRef}
                className={CSS_CLASS_NAME}
                data-test-id={ACTIVE_PAGE_BLOCK_TEST_ID}>
                <PageBlockEditor
                    additionalBlockData={additionalBlockData}
                    block={block}
                    onBlockModified={handleBlockModified}
                    onBlockSaved={handleBlockSaved}
                    onBlockSaving={handleBlockSaving}
                    onDelete={onDelete}
                    onDone={onDone}
                    onMoveDown={onMoveDown}
                    onMoveUp={onMoveUp}
                />
                <div className={`${CSS_CLASS_NAME}__buttonGroup`}>
                    {saveState === SaveState.Saved && (
                        <span className={`${CSS_CLASS_NAME}__saveState -saved`}>
                            <Icon type={Icons.Checkmark} />
                            {t("saved")}
                        </span>
                    )}
                    {saveState === SaveState.Saving && (
                        <span
                            className={`${CSS_CLASS_NAME}__saveState -saving`}>
                            <Loader
                                type={LoaderStyles.LinkGlyph}
                                size={LoaderSizes.XSmall}
                            />{" "}
                            {t("saving")}
                        </span>
                    )}
                </div>
            </div>
        </HideIfReadonly>
    );
};

export default ActivePageBlock;
